import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPortalLayout, makeLayoutId } from "../../services/layoutService";
import {
  getLibraryIndex,
  mergeTopicWithLibrary,
} from "../../services/topicLibraryService";
import { hasQuiz } from "../../data/quiz-library";
import PortalMediaPopup from "./PortalMediaPopup";
import AvatarMaker from "../../components/avatar/AvatarMaker";
import { getAuth } from "firebase/auth";
import { doc, getDoc, setDoc, collection, getDocs, onSnapshot } from "firebase/firestore";
import FunPopup from "../../components/common/FunPopup";
import BuyCoinsButton from "../../components/coins/BuyCoinsButton";
import { db } from "../../firebase/firebaseConfig";
import { listRoster, lookupEducatorByCode } from "../../utils/educatorCode";
import useAccessLevel, { ACCESS } from "../../hooks/useAccessLevel";
import FreeTierBanner from "../../components/FreeTierBanner";
import WelcomeHeading from "../../components/WelcomeHeading";
import PortalSubjectNav from "../../components/PortalSubjectNav";
import AdminBackButton from "../../components/AdminBackButton";
import { subjectLabel, SUBJECTS } from "../../data/subjects";
import "./subjectPage.css";

import SubscribeNow from "../../components/SubscribeNow";
// 🪙 Coins: the student's counter, and the teacher's Give coins bar.
import CoinCounter from "../../components/coins/CoinCounter";
import GiveCoinsBar from "../../components/coins/GiveCoinsBar";
import GiveCoinsPopup from "../../components/coins/GiveCoinsPopup";
import ShopButton from "../../components/shop/ShopButton";
import FocusAvatar from "../../components/avatar/FocusAvatar";
import MyAvatar from "../../components/avatar/MyAvatar";
import LeaderboardButton from "../../components/leaderboard/LeaderboardButton";
import RewardsBar from "../../components/rewards/RewardsBar";
import GiveChestButton from "../../components/rewards/GiveChestButton";
import CustomMaterialForm from "../../components/customMaterial/CustomMaterialForm";
import { orderTopics } from "../../utils/standardOrder";
import "./layoutBuilder.css";
import "../../styles/sbl-ui.css";
import "../../styles/admin-back-button.css";
import "./portalAccess.css";

const subjectIcon = (v) => SUBJECTS.find((x) => x.value === v)?.icon || "📚";

const brightStar =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fbright_star.png?alt=media&token=b033c5b7-788e-401c-b650-98399c1e8551";

const blankStar =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fblank_star.png?alt=media&token=0dd0e339-3016-4f1c-8db2-eae91a16c21a";

const defaultLogo =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f";

const DynamicPortalPage = () => {
  const { state, grade, subject, view } = useParams();
  const navigate = useNavigate();

  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mediaPopup, setMediaPopup] = useState(null);

  // Slugs with a quiz document in Firestore. Admin-authored quizzes live there
  // and are invisible to the static library, so a topic can be playable
  // without appearing in it.
  const [quizzesInFirestore, setQuizzesInFirestore] = useState(new Set());

  const [showEducatorControls, setShowEducatorControls] = useState(false);
  const [topicVisibility, setTopicVisibility] = useState(null);
  const [pop, setPop] = useState(null); // the cartoony "Saved!" / error popup

  const [progressData, setProgressData] = useState({});

  const [showStudentsPopup, setShowStudentsPopup] = useState(false);
  const [showGive, setShowGive] = useState(false); // 🎁 Give coins popup
  // What the student is wearing from the shop (live).
  const [classStudents, setClassStudents] = useState([]);
  const [studentProgressMap, setStudentProgressMap] = useState({});
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [currentUserData, setCurrentUserData] = useState(null);
  const [showAvatarPopup, setShowAvatarPopup] = useState(false);

  // Only admins should ever see the builder prompt. Everyone else — free,
  // trial, paying subscriber — was being shown "No Layout Found", the raw
  // layout ID, and a Build This Page button that routes to an admin-only
  // route they'd bounce off. That reads as a broken product to a customer.
  const {
    level,
    contentLimit,
    contentLimitForCourse,
    isFree,
    hasPaidSubscription,
    profile,
    customContent: canRequestCustom,
    studentTracking,
  } = useAccessLevel();
  // Request Custom Material pop-up (subscribers send; trial users can fill it in)
  const [customOpen, setCustomOpen] = useState(false);
  // after the free trial a teacher still sees who is in the class (names and
  // avatars) but not their scores
  const scoresLocked = view === "educator" && !studentTracking;

  // Which locked topic the person just tried to open. Holding the topic
  // rather than a boolean lets the message name the lesson they wanted,
  // which reads as an explanation instead of a wall.
  const [lockedTopic, setLockedTopic] = useState(null);
  const isAdmin = level === ACCESS.ADMIN;

  const layoutId = useMemo(
    () => makeLayoutId({ state, grade, subject, view }),
    [state, grade, subject, view]
  );

  useEffect(() => {
    const loadLayout = async () => {
      try {
        setLoading(true);
        const data = await getPortalLayout(layoutId);

        // TOPIC LIBRARY RESOLUTION
        // ------------------------
        // Merged here, once, at the single point where a layout enters the
        // page — rather than inside videoOf/musicOf. Every downstream reader
        // (the thumbnail, the Video and Music buttons, hasGame, the lyrics in
        // PortalMediaPopup, gameType) then sees a resolved topic for free.
        // Patching the two media accessors instead would have fixed the two
        // buttons and left the image and the lyrics still reading the raw
        // layout, which is exactly the half-working state that is hardest to
        // diagnose.
        //
        // Direction: the LAYOUT WINS on anything it actually sets, and the
        // library only fills blanks. A page that already carries its own
        // videoUrl keeps it; a field left empty picks the library's up. That
        // is what makes this safe to ship without migrating anything — no
        // existing page changes unless it had a hole.
        //
        // Only PUBLISHED library topics resolve (getLibraryIndex filters on
        // status), so a draft can be edited and reviewed without touching a
        // single classroom.
        let resolved = data;

        if (data?.topics?.length) {
          try {
            const library = await getLibraryIndex();
            resolved = {
              ...data,
              topics: data.topics.map((topic) => {
                const canonical = library[String(topic?.slug || "").toLowerCase()];
                return canonical ? mergeTopicWithLibrary(topic, canonical) : topic;
              }),
            };
          } catch (libraryError) {
            // A rules change, an offline client, or an empty collection must
            // never blank a page that renders perfectly well on its own
            // inline data. Fall through with the unmerged layout.
            console.warn(
              "[DynamicPortalPage] topic library unavailable:",
              libraryError?.code || libraryError?.message
            );
          }
        }

        setLayout(resolved);
      } catch (error) {
        console.error("Error loading dynamic portal layout:", error);
      } finally {
        setLoading(false);
      }
    };

    loadLayout();
  }, [layoutId]);

  useEffect(() => {
    loadCurrentUserData();
  }, []);

  useEffect(() => {
    if (layout) {
      fetchProgress();
    }
  }, [layout]);

  useEffect(() => {
    if (!layout) return;

    if (view === "student") {
      // live: when the teacher saves Educator Controls, this page changes
      // straight away (no refresh)
      let stop = () => {};
      let alive = true;
      loadStudentEducatorControls().then((unsub) => {
        if (alive && typeof unsub === "function") stop = unsub;
        else if (typeof unsub === "function") unsub();
      });
      return () => {
        alive = false;
        stop();
      };
    } else if (view === "educator") {
      // Was missing entirely: an educator's saved toggles were only ever read
      // back on the STUDENT page, so reopening Educator Controls always
      // showed every box ticked and a Save wrote a fresh all-visible map over
      // whatever they'd set last week.
      loadEducatorControls();
    }
  }, [layout, view]);

  const loadCurrentUserData = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) return;

    try {
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        setCurrentUserData({
          uid: user.uid,
          ...userSnap.data(),
        });
      }
    } catch (error) {
      console.error("Error loading current user data:", error);
    }
  };

  // (The old picture picker's save handler lived here. The avatar maker
  // saves through the saveAvatarLook Cloud Function instead; old picture
  // avatars still show until a student makes a new one.)


  // MEDIA KEY DRIFT
  // ---------------
  // LayoutBuilder writes videoUrl/musicUrl. Every hand-authored topics file in
  // src/data — and the shared ngss-science, ccss-math and ca-socialstudies
  // libraries built from them — uses video/music. A layout seeded from those
  // files rendered with a Game button and nothing else, which reads as "the
  // media did not attach" rather than "the two key names disagree".
  //
  // Reading both spellings repairs every already-seeded layout in place with
  // no re-seed. The libraries have been renamed to videoUrl/musicUrl at the
  // source too; this stays as the shim for documents written before that, and
  // for the legacy per-state files, which still use the old keys.
  const videoOf = (topic) => topic?.videoUrl || topic?.video || "";
  const musicOf = (topic) => topic?.musicUrl || topic?.music || "";
  // The sing-along MP4 (topicLibrary.singAlongUrl, set by the Sing-along
  // studio or by hand in the Topic Library). No URL = no button and no star.
  const singAlongOf = (topic) => topic?.singAlongUrl || "";

  // A Game button that opens an empty game is worse than no button.
  //
  // The button used to appear for any topic with a slug. Only 62% of math
  // topics have questions authored, 48% of science, 33% of California social
  // studies — so most buttons led to a dead end, and clicking two or three of
  // those is indistinguishable from "games are broken in this state".
  //
  // Playable means: inline questions, a quiz document in Firestore, or an
  // entry in the shared library. Firestore is checked by the effect below;
  // until it resolves the static library answers, which never shows a button
  // that would dead-end.
  const hasGame = (topic) => {
    if (!topic?.slug) return false;
    if ((topic.questions || []).length > 0) return true;
    if (quizzesInFirestore.has(String(topic.slug).toLowerCase())) return true;
    return hasQuiz(topic.slug);
  };

  // A layout written by anything other than LayoutBuilder (a seeder, a manual
  // Firestore edit) can be missing `tools` entirely. Treated as absent, that
  // hid the Video/Music/Game buttons on every seeded page. Missing means "use
  // the builder's defaults"; an explicit false still means off.
  const isToolOn = (toolName) => {
    if (!layout?.tools) return true;
    return layout.tools[toolName] !== false;
  };

  useEffect(() => {
    if (!layout?.topics?.length) return;

    let cancelled = false;

    (async () => {
      try {
        const snap = await getDocs(collection(db, "quizzes"));
        if (cancelled) return;

        const found = new Set();
        snap.forEach((docSnap) => {
          if ((docSnap.data()?.questions || []).length) {
            found.add(docSnap.id.toLowerCase());
          }
        });
        setQuizzesInFirestore(found);
      } catch (err) {
        // Rules change, offline client — fall back to the static library.
        // Never blocks the page.
        console.warn(
          "[DynamicPortalPage] quiz list unavailable:",
          err?.code || err?.message
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [layout]);

  const handleHomeClick = () => {
    if (view === "educator") {
      navigate("/educator/dashboard");
    } else if (view === "student") {
      navigate("/dashboard");
    } else {
      navigate("/admin");
    }
  };

  const handleSubjectClick = (nextSubject) => {
    navigate(`/portal/${state}/${grade}/${nextSubject}/${view}`);
  };

  const handleOpenVideo = (topic) => {
    setMediaPopup({
      type: "video",
      topic,
    });
  };

  const handleOpenSingAlong = (topic) => {
    setMediaPopup({
      type: "singalong",
      topic,
    });
  };

  const handleOpenMusic = (topic) => {
    setMediaPopup({
      type: "music",
      topic,
    });
  };

  const handleClosePopup = () => {
    setMediaPopup(null);
  };

  const handleProgressRefresh = () => {
    fetchProgress();
  };

  const handleGameClick = (topic) => {
    // Built from the route params, not layout.*. The layout doc is only
    // guaranteed to have the fields whoever wrote it chose to write - a
    // seeded doc can be keyed va_3rd_science_educator and still carry no
    // `view` field, which silently produced /dynamic-treasure-map/.../undefined/...
    // and a "No Treasure Map Layout Found" dead end. The params always match
    // the page the person is standing on.
    navigate(
      `/dynamic-treasure-map/${state}/${grade}/${subject}/${view}/${topic.slug}`
    );
  };

  const fetchProgress = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user || !layout) return;

    try {
      const subjectKey = `${layout.state}_${layout.grade}_${layout.subject}`;
      const userDataRef = doc(db, "userData", user.uid);
      const userDataSnap = await getDoc(userDataRef);
      const userData = userDataSnap.data() || {};
      const subjectProgress = userData[subjectKey] || {};

      const progressMap = {};

      (layout.topics || []).forEach((topic) => {
        if (!topic.slug) return;

        const item = subjectProgress[topic.slug] || {};
        const gameValue = item.game;

        progressMap[topic.slug] = {
          video: Boolean(item.video),
          music: Boolean(item.music),
          singalong: Boolean(item.singalong),
          game:
            typeof gameValue === "boolean"
              ? gameValue
              : Boolean(gameValue?.completed),
          score:
            typeof gameValue === "object" && gameValue?.score
              ? Number(gameValue.score) || 0
              : 0,
        };
      });

      setProgressData(progressMap);
    } catch (error) {
      console.error("Error loading progress:", error);
    }
  };

  const buildStudentProgress = async (students) => {
    const subjectKey = `${layout.state}_${layout.grade}_${layout.subject}`;
    const progressResults = {};

    for (const student of students) {
      const userDataRef = doc(db, "userData", student.id);
      const userDataSnap = await getDoc(userDataRef);
      const userData = userDataSnap.data() || {};
      const subjectProgress = userData[subjectKey] || {};

      let videoComplete = 0;
      let musicComplete = 0;
      let gameComplete = 0;
      let totalScore = 0;

      const topicDetails = (layout.topics || []).map((topic) => {
        const progress = subjectProgress[topic.slug] || {};

        const gameValue = progress.game;
        const completedGame =
          typeof gameValue === "boolean"
            ? gameValue
            : Boolean(gameValue?.completed);

        const score =
          typeof gameValue === "object" && gameValue?.score
            ? Number(gameValue.score) || 0
            : 0;

        if (progress.video) videoComplete += 1;
        if (progress.music) musicComplete += 1;
        if (completedGame) gameComplete += 1;
        totalScore += score;

        return {
          title: topic.title || "Untitled Topic",
          slug: topic.slug,
          standardNumber: topic.standardNumber || "",
          video: Boolean(progress.video),
          music: Boolean(progress.music),
          singalong: Boolean(progress.singalong),
          game: completedGame,
          score,
        };
      });

      progressResults[student.id] = {
        videoComplete,
        musicComplete,
        gameComplete,
        totalScore,
        totalTopics: layout.topics?.length || 0,
        topicDetails,
      };
    }

    return progressResults;
  };

  // The educator's OWN controls for this class, so the popup opens showing
  // what they actually saved.
  const loadEducatorControls = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user || !layout) return;

    try {
      const controlsId = `${user.uid}_${layout.state}_${layout.grade}_${layout.subject}`;
      const snap = await getDoc(doc(db, "educatorPortalControls", controlsId));

      if (snap.exists() && snap.data().topicVisibility) {
        setTopicVisibility(snap.data().topicVisibility);
      }
    } catch (error) {
      console.error("Error loading educator controls:", error);
    }
  };

  const saveEducatorControls = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user || !layout) {
      setPop({ tone: "bad", title: "Please sign in", text: "You must be signed in to save educator controls." });
      return;
    }

    try {
      const controlsId = `${user.uid}_${layout.state}_${layout.grade}_${layout.subject}`;

      // A complete slug -> boolean map rather than whatever partial object
      // the toggles happened to build. `topicVisibility` starts as null, so
      // saving without touching anything used to write null and the student
      // page then read null back and showed everything.
      const visibilityMap = {};
      (layout.topics || []).forEach((topic) => {
        if (!topic.slug) return;
        visibilityMap[topic.slug] = topicVisibility?.[topic.slug] !== false;
      });

      await setDoc(
        doc(db, "educatorPortalControls", controlsId),
        {
          educatorUid: user.uid,
          state: layout.state,
          grade: layout.grade,
          subject: layout.subject,
          layoutId,
          topicVisibility: visibilityMap,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setTopicVisibility(visibilityMap);
      setShowEducatorControls(false);
      const hidden = Object.values(visibilityMap).filter((v) => !v).length;
      setPop({
        tone: "ok",
        icon: "🎛️",
        title: "Controls saved!",
        text: hidden
          ? `${hidden} topic${hidden === 1 ? " is" : "s are"} now hidden from your students — their pages update right away.`
          : "Every topic is showing for your students — their pages update right away.",
      });
    } catch (error) {
      console.error("Error saving educator controls:", error);
      setPop({
        tone: "bad",
        title: "Couldn't save",
        text:
          error?.code === "permission-denied"
            ? "Your account isn't recognised as an educator on this class. Sign out and back in, then try again."
            : "Something went wrong saving your controls. Please try again.",
      });
    }
  };

  const loadClassStudents = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user || !layout) return;
    if (showStudentsPopup) return;

    try {
      const educatorRef = doc(db, "users", user.uid);
      const educatorSnap = await getDoc(educatorRef);

      if (!educatorSnap.exists()) {
        setPop({ tone: "bad", title: "Hmm…", text: "We couldn't find your educator account." });
        return;
      }

      const educatorData = educatorSnap.data();
      const educatorCode = educatorData.educatorCode;

      if (!educatorCode) {
        setPop({ tone: "bad", title: "No class code yet", text: "Your class code hasn't been made yet — open your dashboard and it will be created." });
        return;
      }

      // Reads educatorRosters/{code}/students rather than querying /users.
      // The old query needed `list` on /users, which rules grant to admins
      // only — so this failed for every educator. It also filtered on a
      // student role that nobody holds until Stripe promotes them, so it
      // would have returned nothing during a trial even with permission.
      const students = await listRoster(educatorCode);

      // scores are only read while the teacher has access to them
      const progressResults = scoresLocked ? {} : await buildStudentProgress(students);

      setClassStudents(students);
      setStudentProgressMap(progressResults);
      setSelectedStudent(null);
      setShowStudentsPopup(true);
    } catch (error) {
      console.error("Error loading students:", error);
      setPop({ tone: "bad", title: "Couldn't load students", text: "Please try again in a moment." });
    }
  };

  const loadStudentEducatorControls = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user || !layout || view !== "student") return;

    try {
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) return;

      const studentData = userSnap.data();
      const educatorCode = studentData.educatorCode;

      if (!educatorCode) return;

      // Was a query on /users filtered by educatorCode AND role. Two reasons
      // it could never work: rules grant `list` on /users to admins only, so
      // it threw permission-denied for every student; and it matched on a
      // granted role like "va-3rd-educator", which a trialling teacher does
      // not hold, so it would have returned nothing even with permission.
      //
      // educatorDirectory/{code} is the registered code -> uid mapping, it is
      // readable by any signed-in user, and it is create-once so it can't be
      // hijacked. One get instead of a query.
      const educator = await lookupEducatorByCode(educatorCode);
      if (!educator?.uid) return;

      const educatorUid = educator.uid;

      const controlsId = `${educatorUid}_${layout.state}_${layout.grade}_${layout.subject}`;
      const controlsRef = doc(db, "educatorPortalControls", controlsId);
      return onSnapshot(
        controlsRef,
        (controlsSnap) => {
          const controlsData = controlsSnap.exists() ? controlsSnap.data() : {};
          setTopicVisibility(controlsData.topicVisibility || null);
        },
        (err) => console.error("Error listening to educator controls:", err)
      );
    } catch (error) {
      console.error("Error loading student educator controls:", error);
    }
    return undefined;
  };

  const toggleTopicVisibility = (slug) => {
    setTopicVisibility((prev) => ({
      ...(prev || {}),
      [slug]: prev?.[slug] === false ? true : false,
    }));
  };

  const openStudentDetail = (student) => {
    setSelectedStudent(student);
  };

  const closeStudentsPopup = () => {
    setShowStudentsPopup(false);
    setSelectedStudent(null);
  };

  // The students popup, extracted so the empty-subject page can show it too.
  // View Students works on a subject with no topics — the roster and each
  // child's progress are class-wide, not per-topic, so there is nothing about
  // an empty subject that makes them unavailable.
  const renderStudentsPopup = () => (
      <div className="portal-popup-overlay">
        <div className="portal-popup-box portal-students-popup">
          <button onClick={closeStudentsPopup} className="portal-popup-close">
            ✖
          </button>

          {!selectedStudent ? (
            <>
              <h2>Students in This Class</h2>
              <button type="button" className="portal-manage-class" onClick={() => navigate("/my-class")}>
                📝 Add, pause or remove students
              </button>
              {scoresLocked ? (
                <div className="portal-scores-locked">
                  <span aria-hidden="true">🔒</span>
                  <div>
                    <strong>Your free trial has ended — your students are still here.</strong>
                    <p>
                      Their progress is saved. Subscribe to see scores and topic progress, give coins and add students again.
                    </p>
                  </div>
                  <button type="button" className="sbl-btn sbl-btn-primary" onClick={() => navigate("/choose-plan")}>
                    See plans
                  </button>
                </div>
              ) : (
                <p>Click a student to view detailed topic progress.</p>
              )}

              {!scoresLocked && classStudents.length > 0 && currentUserData?.educatorCode && (
                <p>
                  <button type="button" className="sp-give-btn" onClick={() => setShowGive(true)}>
                    🎁 Give coins
                  </button>
                </p>
              )}
              {!scoresLocked && classStudents.length > 0 && <GiveChestButton students={classStudents} label="the whole class" />}
              {currentUserData?.educatorCode && (
                <p className="sbl-lb-teacher-entry">
                  <LeaderboardButton code={currentUserData.educatorCode} isEducator label="Class leaderboard" />
                </p>
              )}

              {classStudents.length === 0 ? (
                <p>No students found with this educator code.</p>
              ) : (
                <div className="student-list-grid">
                  {classStudents.map((student) => (
                    <button
                      key={student.id}
                      type="button"
                      className={`student-list-card ${scoresLocked ? "is-locked" : "student-list-card-clickable"}`}
                      onClick={() => !scoresLocked && openStudentDetail(student)}
                      aria-disabled={scoresLocked}
                    >
                      <div className="student-avatar">
                        <FocusAvatar id={student.id || student.uid} size={84} fullSize={170} name={student.firstName || ""} />
                      </div>

                      <h3>
                        {student.firstName || ""} {student.lastName || ""}
                      </h3>

                      {scoresLocked ? (
                        <p className="student-scores-locked">🔒 Scores locked</p>
                      ) : (
                      <>
                      <p>{student.email || ""}</p>
                      <p>
                        <strong>Role:</strong> {student.role}
                      </p>

                      <div className="student-progress-summary">
                        <p>
                          🎬 Video:{" "}
                          {studentProgressMap[student.id]?.videoComplete ||
                            0}
                          /{studentProgressMap[student.id]?.totalTopics || 0}
                        </p>

                        <p>
                          🎵 Music:{" "}
                          {studentProgressMap[student.id]?.musicComplete ||
                            0}
                          /{studentProgressMap[student.id]?.totalTopics || 0}
                        </p>

                        <p>
                          🎮 Games:{" "}
                          {studentProgressMap[student.id]?.gameComplete || 0}
                          /{studentProgressMap[student.id]?.totalTopics || 0}
                        </p>

                        <p>
                          ⭐ Score:{" "}
                          {studentProgressMap[student.id]?.totalScore || 0}
                        </p>
                      </div>
                      </>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="student-detail-view">
              <button
                type="button"
                className="student-back-button"
                onClick={() => setSelectedStudent(null)}
              >
                ← Back to Student List
              </button>

              <div className="student-detail-header">
                <div className="student-avatar large">
                  <FocusAvatar id={selectedStudent.id || selectedStudent.uid} size={110} fullSize={220} name={selectedStudent.firstName || ""} />
                </div>

                <div>
                  <h2>
                    {selectedStudent.firstName || ""}{" "}
                    {selectedStudent.lastName || ""}
                  </h2>
                  <p>{selectedStudent.email || ""}</p>
                </div>
              </div>

              <GiveCoinsBar
                students={[selectedStudent]}
                label={selectedStudent.firstName || "this student"}
              />

              <div className="student-detail-summary">
                <div>
                  <strong>🎬 Videos</strong>
                  <span>
                    {studentProgressMap[selectedStudent.id]?.videoComplete ||
                      0}
                    /
                    {studentProgressMap[selectedStudent.id]?.totalTopics || 0}
                  </span>
                </div>

                <div>
                  <strong>🎵 Music</strong>
                  <span>
                    {studentProgressMap[selectedStudent.id]?.musicComplete ||
                      0}
                    /
                    {studentProgressMap[selectedStudent.id]?.totalTopics || 0}
                  </span>
                </div>

                <div>
                  <strong>🎮 Games</strong>
                  <span>
                    {studentProgressMap[selectedStudent.id]?.gameComplete ||
                      0}
                    /
                    {studentProgressMap[selectedStudent.id]?.totalTopics || 0}
                  </span>
                </div>

                <div>
                  <strong>⭐ Score</strong>
                  <span>
                    {studentProgressMap[selectedStudent.id]?.totalScore || 0}
                  </span>
                </div>
              </div>

              <div className="student-topic-progress-list">
                {(studentProgressMap[selectedStudent.id]?.topicDetails || [])
                  .length === 0 ? (
                  <p>No topic progress found yet.</p>
                ) : (
                  studentProgressMap[selectedStudent.id].topicDetails.map(
                    (topic, index) => (
                      <div
                        key={`${topic.slug || topic.title}-${index}`}
                        className="student-topic-progress-row"
                      >
                        <div>
                          <h3>{topic.title}</h3>
                          {topic.standardNumber && (
                            <p>{topic.standardNumber}</p>
                          )}
                        </div>

                        <div className="student-topic-badges">
                          <span
                            className={
                              topic.video
                                ? "progress-badge complete"
                                : "progress-badge incomplete"
                            }
                          >
                            🎬 {topic.video ? "Done" : "Not Started"}
                          </span>

                          <span
                            className={
                              topic.music
                                ? "progress-badge complete"
                                : "progress-badge incomplete"
                            }
                          >
                            🎵 {topic.music ? "Done" : "Not Started"}
                          </span>

                          <span
                            className={
                              topic.game
                                ? "progress-badge complete"
                                : "progress-badge incomplete"
                            }
                          >
                            🎮 {topic.game ? "Done" : "Not Started"}
                          </span>

                          <span className="progress-badge score">
                            ⭐ {topic.score || 0}
                          </span>
                        </div>
                      </div>
                    )
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
  );
  // ==========================================================================
  // EDUCATOR ACTIONS — rendered on EVERY subject, including empty ones
  // ==========================================================================
  // This row used to live inline in the has-a-layout branch only. "Other" has
  // no seeded layout, so the early return fired first and an educator opening
  // Other lost Educator Controls, Request Custom Material and View Students
  // entirely.
  //
  // That's backwards from what those buttons are for. An empty subject is
  // exactly when a teacher wants to ORDER material for it — the request form
  // is the thing that fills the empty shelf. Hiding it on the one page with
  // nothing on it means the only route to custom material is a subject that
  // already has content.
  //
  // Educator Controls is the one exception: it toggles topic visibility, and
  // with no topics there is nothing to toggle, so it's disabled with a reason
  // rather than removed. A button that vanishes reads as broken; a button
  // that explains itself doesn't.
  const renderEducatorActions = (hasTopics = true) => {
    if (view !== "educator") return null;

    return (
      <div className="portal-educator-actions sbl-tile-row">
        <button
          type="button"
          className="sbl-tile sbl-tile-educator"
          onClick={() => setShowEducatorControls(true)}
          disabled={!hasTopics}
          aria-disabled={!hasTopics}
          title={
            hasTopics
              ? undefined
              : "There are no topics to show or hide in this subject yet."
          }
        >
          <span className="sbl-tile-icon" aria-hidden="true">🎛️</span>
          <span className="sbl-tile-label">Educator Controls</span>
        </button>

        <button
          type="button"
          className="sbl-tile sbl-tile-home"
          onClick={() => setCustomOpen(true)}
        >
          <span className="sbl-tile-icon" aria-hidden="true">✏️</span>
          <span className="sbl-tile-label">Request Custom Material</span>
        </button>

        <button
          type="button"
          className="sbl-tile sbl-tile-classroom"
          onClick={loadClassStudents}
        >
          <span className="sbl-tile-icon" aria-hidden="true">👩‍🏫</span>
          <span className="sbl-tile-label">View Students</span>
        </button>

        <button
          type="button"
          className="sbl-tile sbl-tile-classroom"
          onClick={() => navigate("/my-class")}
        >
          <span className="sbl-tile-icon" aria-hidden="true">📝</span>
          <span className="sbl-tile-label">Manage My Class</span>
        </button>
      </div>
    );
  };

  if (loading) {
    return <div className="dynamic-portal-page">Loading page...</div>;
  }

  // ==========================================================================
  // A SUBJECT WITH NO LAYOUT YET IS STILL A PAGE
  // ==========================================================================
  // This used to return a bare card on an empty background — no logo, no
  // subject tiles, no way anywhere except a Back button. "Other" has no
  // seeded layout, so opening it from the dashboard dropped you onto a
  // dead-end screen that reads exactly like being denied access, even though
  // the route guard had let you straight through.
  //
  // The page now renders in full — same logo, same tiles, same welcome, the
  // subject you asked for marked active — and the "nothing here yet" message
  // sits in the topic grid where the topics would be. An empty subject looks
  // like an empty subject instead of a locked one, and every other subject
  // stays one tap away.
  if (!layout) {
    return (
      <div className={`dynamic-portal-page portal-subject-${subject}`}>
        <img src={defaultLogo} alt="Smart Board Learning" className="portal-logo" />

        <PortalSubjectNav
          state={state}
          grade={grade}
          view={view}
          activeSubject={subject}
          onSubjectClick={handleSubjectClick}
          onHomeClick={handleHomeClick}
        />

        <header className="dynamic-portal-title-area">
          <h1>{`${String(state || "").toUpperCase()} ${grade || ""} ${subjectLabel(subject)}`.trim()}</h1>
          <WelcomeHeading variant="class" subject={subject} as="h2" className="portal-welcome" />
        </header>

        <section className="sp-grid">
          <div className="missing-layout-card">
            {isAdmin ? (
              <>
                <h2>No layout yet</h2>
                <p>
                  Nothing has been built for <strong>{layoutId}</strong> yet.
                </p>
                <button onClick={() => navigate("/admin/layout-builder")}>
                  Build this page
                </button>
              </>
            ) : (
              <>
                <h2>New material coming soon</h2>
                <p>
                  {view === "educator"
                    ? "We're still building this subject for your grade. You can request custom material for it below, or pick another subject above."
                    : "We're still building this subject for your grade. Everything else is ready — pick another subject above and carry on."}
                </p>
              </>
            )}
          </div>
        </section>

        {/* The educator tools belong here MORE than on a full page, not less:
            an empty subject is exactly when a teacher wants to order material
            for it. `false` disables Educator Controls, since there are no
            topics to show or hide yet. */}
        {renderEducatorActions(false)}

        {showStudentsPopup && renderStudentsPopup()}
        {view === "educator" && (
          <CustomMaterialForm
            open={customOpen}
            onClose={() => setCustomOpen(false)}
            mode={canRequestCustom ? "live" : "locked"}
            defaults={{ state, grade, subject }}
            from={layoutId}
          />
        )}
        {showGive && currentUserData?.educatorCode && <GiveCoinsPopup code={currentUserData.educatorCode} onClose={() => setShowGive(false)} />}
      </div>
    );
  }

  // in Standard Number order unless the page is set to "as arranged"
  const visibleTopics = orderTopics(layout.topics || [], layout.topicOrder)
    .filter((topic) => {
      if (topic.visible === false) return false;
      if (!topic.slug) return true;

      if (topicVisibility && topicVisibility[topic.slug] === false) {
        return false;
      }

      return true;
    });

  // Free-tier gating.
  //
  // This is where it belongs, and it wasn't here before: the codemod put the
  // topic locking into the ~120 static subject pages, but the dashboards
  // navigate to /portal/... which lands on THIS component. So every seeded
  // subject was showing all topics to everyone regardless of what they paid.
  //
  // Locked topics still render — hiding them leaves nothing to upgrade
  // toward. The tool buttons are what change.
  //
  // Note this counts against visibleTopics, AFTER the educator's visibility
  // toggles have been applied. A teacher who hides the first three topics
  // doesn't accidentally spend a free user's whole allowance on hidden
  // material; the free five are five things they can actually open.
  // Gated PER COURSE, not per account.
  //
  // `grade` here is the URL's track token — "5th" for elementary, "algebra-1"
  // for a high-school course (see src/data/courses.js). On the 6-12
  // single-subject plans that distinction is the whole product: a student who
  // bought Chemistry gets all of Chemistry and the free allowance of Biology.
  //
  // The bare `contentLimit` this replaced could only answer "has this person
  // paid for anything?", so it would have handed a $4.99 Chemistry customer
  // the entire Biology, Algebra 2 and Government libraries as well.
  //
  // Everyone else is unaffected: contentLimitForCourse returns Infinity for
  // admins, trials, seats, granted roles, and every all-subjects
  // subscription — including every subscription written before this existed.
  const freeLimit = contentLimitForCourse
    ? contentLimitForCourse(grade)
    : contentLimit;

  const lockedCount =
    freeLimit === Infinity ? 0 : Math.max(0, visibleTopics.length - freeLimit);

  // the steps a topic offers (only the ones it has and the layout turns on)
  const topicSteps = (topic) =>
    [
      isToolOn("video") && videoOf(topic) && { key: "video", icon: "🎬", label: "Video", open: () => handleOpenVideo(topic) },
      isToolOn("music") && musicOf(topic) && { key: "music", icon: "🎵", label: "Song", open: () => handleOpenMusic(topic) },
      isToolOn("singalong") && singAlongOf(topic) && { key: "singalong", icon: "🎤", label: "Sing-along", open: () => handleOpenSingAlong(topic) },
      isToolOn("game") && hasGame(topic) && { key: "game", icon: "🎮", label: "Game", open: () => handleGameClick(topic) },
    ].filter(Boolean);
  const doneCount = visibleTopics.filter((t) => {
    const st = topicSteps(t);
    const pr = progressData?.[t.slug] || {};
    return st.length > 0 && st.every((x) => pr[x.key]);
  }).length;

  const pageStyle = {
    backgroundImage: layout.backgroundImageUrl
      ? `url(${layout.backgroundImageUrl})`
      : undefined,
  };

  return (
    <div
      className={`dynamic-portal-page theme-${layout.theme || "kid"} portal-subject-${subject}`}
      style={pageStyle}
    >
      {/* Preview lands on the real portal route, so there is no preview
          chrome to close. Admins get a way back; nobody else sees it. */}
      <AdminBackButton isAdmin={isAdmin} />

      {/* ---------- Top card: who, which class, how far along, and the
          buttons — the same design as the dashboards ---------- */}
      <section className={`sp-hero is-${subject}`}>
        <div className="sp-hero-top">
          <img src={layout.logoUrl || defaultLogo} alt="Smart Board Learning" className="sp-logo" />
          <PortalSubjectNav state={state} grade={grade} view={view} activeSubject={subject} onSubjectClick={handleSubjectClick} onHomeClick={handleHomeClick} />
        </div>

        <div className="sp-hero-main">
          <button type="button" className="sp-hero-avatar" onClick={() => setShowAvatarPopup(true)} title={view === "educator" ? "My avatar" : "Change my avatar"}>
            <MyAvatar size={104} />
            <span className="sp-hero-edit">✎ {view === "educator" ? "My avatar" : "Change"}</span>
          </button>
          <div className="sp-hero-text">
            <p className="sp-kicker">{layout.title}</p>
            <h1 className="sp-title">
              <span aria-hidden="true">{subjectIcon(subject)}</span> {subjectLabel(subject)}
            </h1>
            <WelcomeHeading variant="class" subject={subject} as="p" className="sp-welcome" />
            {visibleTopics.length > 0 && (
              <div className="sp-progress" title={`${doneCount} of ${visibleTopics.length} topics finished`}>
                <div className="sp-progress-bar">
                  <i style={{ width: `${Math.round((doneCount / visibleTopics.length) * 100)}%` }} />
                </div>
                <span>
                  {view === "educator" ? "Your topics: " : ""}
                  <b>{doneCount}</b> of <b>{visibleTopics.length}</b> topics finished {doneCount === visibleTopics.length && doneCount > 0 ? "🏆" : ""}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="sp-hero-actions">
          {visibleTopics.length > 0 && (
            <button type="button" className="sp-map-btn" onClick={() => navigate(`/dynamic-treasure-map/${state}/${grade}/${subject}/${view}/map`)}>
              🗺️ Treasure map
            </button>
          )}
          <CoinCounter />
          {view === "student" ? (
            <>
              <ShopButton avatarUrl={currentUserData?.avatar || currentUserData?.avatarUrl || ""} />
              <LeaderboardButton code={currentUserData?.educatorCode || ""} />
              <RewardsBar />
            </>
          ) : (
            <>
              {currentUserData?.educatorCode && (
                <button type="button" className="sp-give-btn" onClick={() => setShowGive(true)} title="Reward the whole class or chosen students">
                  🎁 Give coins
                </button>
              )}
              <ShopButton educator avatarUrl={currentUserData?.avatar || currentUserData?.avatarUrl || ""} />
              <BuyCoinsButton educator />
              {currentUserData?.educatorCode && <LeaderboardButton code={currentUserData.educatorCode} isEducator label="Class leaderboard" />}
            </>
          )}
        </div>
      </section>

      <p className="sp-howto">
        {view === "educator"
          ? "Open a topic's Video, Song or Sing-along on your board, then play its Game together. Manage what students see with Educator Controls at the bottom."
          : "For each topic: 🎬 watch, 🎵 listen, 🎤 sing along, then 🎮 play the game to earn its star!"}
      </p>

      {isFree && lockedCount > 0 && (
        <FreeTierBanner hiddenCount={lockedCount} label="topics" />
      )}

      {/* Subject pages are where someone actually meets the limit, so the
          way past it belongs here too — not only on the dashboard. Compact
          form: a single button, since the pricing detail lives on the
          dashboard block and /choose-plan. */}
      {!hasPaidSubscription && (
        <div className="portal-subscribe-row">
          <SubscribeNow
            profile={profile || {}}
            uid={getAuth().currentUser?.uid}
            compact
          />
        </div>
      )}

      <section className="portal-topic-grid">
        {visibleTopics.length === 0 && (
          <div className="missing-layout-card">
            <h2>No topics yet</h2>
            <p>Add topics from the Layout Builder.</p>
          </div>
        )}

        {visibleTopics.map((topic, index) => {
          const locked = index >= freeLimit;
          const pr = progressData?.[topic.slug] || {};
          const steps = topicSteps(topic);
          const done = steps.filter((st) => pr[st.key]).length;
          const complete = steps.length > 0 && done === steps.length;
          return (
            <article
              className={`sp-card${locked ? " is-locked" : ""}${complete ? " is-done" : ""}`}
              key={`${topic.slug || topic.title}-${index}`}
              style={{ "--h": (index * 47 + 200) % 360 }}
            >
              <div className="sp-card-art">
                {topic.imageUrl ? <img src={topic.imageUrl} alt="" loading="lazy" /> : <span className="sp-card-emoji" aria-hidden="true">{subjectIcon(subject)}</span>}
                <span className="sp-card-num">Topic {index + 1}</span>
                {complete && <span className="sp-card-ribbon">⭐ Complete!</span>}
                {pr.game && !complete && <span className="sp-card-ribbon is-star">⭐</span>}
              </div>

              <div className="sp-card-body">
                <h2 className="sp-card-title">{topic.title || `Topic ${index + 1}`}</h2>
                {(topic.standardNumber || topic.description) && (
                  <p className="sp-card-meta">
                    {topic.standardNumber && <span className="sp-pill">{topic.standardNumber}</span>}
                    {topic.description}
                  </p>
                )}

                {locked ? (
                  <button type="button" className="sp-unlock" onClick={() => setLockedTopic(topic)}>
                    🔒 Unlock this topic
                  </button>
                ) : (
                  <>
                    <div className="sp-steps">
                      {steps.map((st) => (
                        <button
                          key={st.key}
                          type="button"
                          className={`sp-step is-${st.key}${pr[st.key] ? " is-done" : ""}`}
                          onClick={st.open}
                          title={pr[st.key] ? `${st.label} — done ✓${st.key === "game" && pr.score ? ` (score ${pr.score})` : ""}` : st.label}
                        >
                          <span className="sp-step-icon" aria-hidden="true">
                            {st.icon}
                          </span>
                          <span className="sp-step-label">{st.label}</span>
                          {pr[st.key] && (
                            <span className="sp-step-check" aria-label="done">
                              ✓
                            </span>
                          )}
                        </button>
                      ))}
                      {layout.tools?.slideshow && (
                        <button type="button" className="sp-step is-slides" onClick={() => navigate(`/slideshow/${topic.slug || topic.quizId}`)}>
                          <span className="sp-step-icon" aria-hidden="true">
                            🖼️
                          </span>
                          <span className="sp-step-label">Slides</span>
                        </button>
                      )}
                    </div>
                    {steps.length > 0 && (
                      <div className="sp-card-progress">
                        <div className="sp-card-bar">
                          <i style={{ width: `${Math.round((done / steps.length) * 100)}%` }} />
                        </div>
                        <span>
                          {done}/{steps.length}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </article>
          );
        })}
      </section>

      {renderEducatorActions()}

      {lockedTopic && (
        <div
          className="portal-popup-overlay"
          onClick={() => setLockedTopic(null)}
        >
          <div
            className="portal-popup-box portal-locked-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="portal-locked-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setLockedTopic(null)}
              className="portal-popup-close"
              aria-label="Close"
            >
              ✖
            </button>

            <div className="portal-locked-icon" aria-hidden="true">
              🔒
            </div>

            <h2 id="portal-locked-title">
              {lockedTopic.title || "This topic"} needs a paid subscription
            </h2>

            <p className="portal-locked-body">
              Your free plan includes the first {freeLimit}{" "}
              {freeLimit === 1 ? "topic" : "topics"} in every subject. A
              subscription opens the rest — all the videos, songs and games in
              this subject and every other one for this grade.
            </p>

            <div className="portal-locked-actions">
              <button
                type="button"
                className="portal-locked-primary"
                onClick={() => navigate("/choose-plan")}
              >
                See plans
              </button>

              <button
                type="button"
                className="portal-locked-secondary"
                onClick={() => setLockedTopic(null)}
              >
                Keep looking around
              </button>
            </div>
          </div>
        </div>
      )}

      {showEducatorControls && (
        <div className="portal-popup-overlay">
          <div className="portal-popup-box">
            <button
              onClick={() => setShowEducatorControls(false)}
              className="portal-popup-close"
            >
              ✖
            </button>

            <h2>Educator Controls</h2>
            <p>Check the topics you want students to see.</p>

            {/* A topic with `visible: false` is switched off in the Layout
                Builder — SBL hasn't published it yet. The page filters those
                out before anything here runs, so showing them as ticked
                boxes told a teacher they were live when nothing would ever
                render. They stay listed, disabled and labelled, because a
                topic that silently disappears from this list looks like data
                loss. */}
            <div className="educator-control-grid">
              {(layout.topics || []).map((topic, index) => {
                const publishedBySbl = topic.visible !== false;

                return (
                  <label
                    key={topic.slug || index}
                    className={`educator-control-item${
                      publishedBySbl ? "" : " educator-control-item-locked"
                    }`}
                    title={
                      publishedBySbl
                        ? undefined
                        : "Not published yet — this topic isn't available to anyone in this class."
                    }
                  >
                    <input
                      type="checkbox"
                      checked={publishedBySbl && topicVisibility?.[topic.slug] !== false}
                      disabled={!publishedBySbl}
                      onChange={() => toggleTopicVisibility(topic.slug)}
                    />
                    {topic.title || `Topic ${index + 1}`}
                    {!publishedBySbl && (
                      <span className="educator-control-flag">
                        Not published yet
                      </span>
                    )}
                  </label>
                );
              })}
            </div>

            <button onClick={saveEducatorControls}>Save Changes</button>
          </div>
        </div>
      )}

      {showStudentsPopup && renderStudentsPopup()}
        {view === "educator" && (
          <CustomMaterialForm
            open={customOpen}
            onClose={() => setCustomOpen(false)}
            mode={canRequestCustom ? "live" : "locked"}
            defaults={{ state, grade, subject }}
            from={layoutId}
          />
        )}

      <FunPopup pop={pop} onClose={() => setPop(null)} />
      {showAvatarPopup && <AvatarMaker isEducator={view === "educator"} onClose={() => setShowAvatarPopup(false)} />}
      {showGive && view === "educator" && currentUserData?.educatorCode && <GiveCoinsPopup code={currentUserData.educatorCode} onClose={() => setShowGive(false)} />}

      {mediaPopup && (
        <PortalMediaPopup
          type={mediaPopup.type}
          topic={{
            ...mediaPopup.topic,
            videoUrl: videoOf(mediaPopup.topic),
            musicUrl: musicOf(mediaPopup.topic),
            singAlongUrl: singAlongOf(mediaPopup.topic),
          }}
          layout={layout}
          onClose={handleClosePopup}
          onProgressUpdate={handleProgressRefresh}
        />
      )}

      <footer className="portal-footer">
        <p>© Smart Board Learning™</p>
        <p>All rights reserved.</p>
      </footer>
    </div>
  );
};

export default DynamicPortalPage;